import React, { memo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Link, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { FieldTypesEnum } from 'constants/loanActionEditor';
import { entityAssignmentTypes } from 'constants/options';
import { DataType } from 'constants/taskCategories';
import { TaskAssignmentType, taskAssignmentTypeOptions } from 'constants/tasks';

import { selectFormsInfoListByGroupNameOptions } from 'store/forms/selectors';
import { selectSlaGroupsOptions } from 'store/underwriting/selectors';

import useFormContext from './hooks/useFormContext';
import { wrapperClassname } from './LoanActionItem.constants';
import { DynamicValueInput } from './LoanActionItems';
import {
  assignTaskBorrowerTargetOptions,
  PRESELECT_BORROWERS_LINK_TEXT,
  USE_PRIMARY_BORROWER_LINK_TEXT,
} from './LoanActionTaskRow.constants';
import {
  getAssignTaskBorrowerTargetsHelperText,
  getAssignTaskEntityAssignmentTypeOption,
  isBorrowerScopedTaskType,
} from './LoanActionTaskRow.utils';

const LoanActionTaskRow = ({ index, rootPath, options, isDisabled, isEditAllowed, label = '' }) => {
  const { setInForm, form } = useFormContext();

  const isOldTaskAssignmentEngine = useFeatureToggle(FeatureFlag.OldTaskAssignmentEngine);

  const slaGroupOptions = useSelector(selectSlaGroupsOptions);
  const formListOptions = useSelector(selectFormsInfoListByGroupNameOptions);

  const isNewTaskAssignmentEngine = !isOldTaskAssignmentEngine;

  const formKey = `${rootPath}[${index}]`;
  const taskTypePath = `${formKey}.taskType`;
  const assignToPath = `${formKey}.assignTo`;
  const slaGroupPath = `${formKey}.slaGroup`;
  const entityAssignmentTypePath = `${formKey}.entityAssignmentType`;
  const loanFormPath = `${formKey}.loanForm`;
  const borrowerTargetsPath = `${formKey}.borrowerTargets`;

  const taskType = _.get(form, taskTypePath);
  const borrowerTargets = _.get(form, borrowerTargetsPath);

  const isSlaGroupsAssigneeSelected = taskType?.taskAssignmentType === TaskAssignmentType.SLA_GROUP;
  const isFormsTaskType = taskType?.dataType === DataType.FORMS;
  const [isBorrowerTargetsPreselectOpen, setIsBorrowerTargetsPreselectOpen] = useState(false);

  const isBorrowerTargetAllowed = isNewTaskAssignmentEngine && isBorrowerScopedTaskType(taskType);
  // Preselecting borrowers is optional: with none picked the step assigns the primary borrower, as before.
  const isBorrowerTargetShown =
    isBorrowerTargetAllowed && (isBorrowerTargetsPreselectOpen || !_.isEmpty(borrowerTargets));
  const borrowerTargetsHelperText = getAssignTaskBorrowerTargetsHelperText(borrowerTargets);

  const onGroupOptionsBy = useCallback(({ categoryName }) => categoryName, []);

  const onChange = useCallback(
    (e, taskType) => {
      const assignmentType = taskAssignmentTypeOptions.find(
        ({ value }) => value === taskType.taskAssignmentType,
      );

      setIsBorrowerTargetsPreselectOpen(false);
      setInForm(assignToPath, assignmentType);
      setInForm(borrowerTargetsPath, []);

      if (!isNewTaskAssignmentEngine) {
        return;
      }

      setInForm(entityAssignmentTypePath, getAssignTaskEntityAssignmentTypeOption(taskType, []));

      const isSlaGroupsAssigneeSelected =
        taskType.taskAssignmentType === TaskAssignmentType.SLA_GROUP;

      if (!isSlaGroupsAssigneeSelected) {
        return;
      }

      const slaGroup = slaGroupOptions.find(({ value }) => value === taskType.slaGroupId);

      setInForm(slaGroupPath, slaGroup);
    },
    [
      assignToPath,
      borrowerTargetsPath,
      entityAssignmentTypePath,
      isNewTaskAssignmentEngine,
      setInForm,
      slaGroupOptions,
      slaGroupPath,
    ],
  );

  const onBorrowerTargetsChange = useCallback(
    (e, selectedBorrowerTargets) =>
      setInForm(
        entityAssignmentTypePath,
        getAssignTaskEntityAssignmentTypeOption(taskType, selectedBorrowerTargets),
      ),
    [entityAssignmentTypePath, setInForm, taskType],
  );

  const onPreselectBorrowersClick = useCallback(() => setIsBorrowerTargetsPreselectOpen(true), []);

  const onUsePrimaryBorrowerClick = useCallback(() => {
    setIsBorrowerTargetsPreselectOpen(false);
    setInForm(borrowerTargetsPath, []);
    setInForm(entityAssignmentTypePath, getAssignTaskEntityAssignmentTypeOption(taskType, []));
  }, [borrowerTargetsPath, entityAssignmentTypePath, setInForm, taskType]);

  return (
    <div className={wrapperClassname}>
      <div className="flex">
        <DynamicValueInput
          required
          className="grow"
          disabled={isDisabled || !isEditAllowed}
          groupBy={onGroupOptionsBy}
          label={label}
          name={taskTypePath}
          options={options}
          type={FieldTypesEnum.SELECT}
          onChange={onChange}
        />
      </div>
      <DynamicValueInput
        disabled
        required
        className="grow"
        label="Assign To"
        name={assignToPath}
        options={taskAssignmentTypeOptions}
        type={FieldTypesEnum.SELECT}
      />
      {isSlaGroupsAssigneeSelected && (
        <DynamicValueInput
          disabled
          className="grow"
          label="SLA Group"
          name={slaGroupPath}
          options={slaGroupOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}
      {isNewTaskAssignmentEngine && (
        <DynamicValueInput
          disabled
          className="grow"
          label="Entity Assignment Type"
          name={entityAssignmentTypePath}
          options={entityAssignmentTypes}
          type={FieldTypesEnum.SELECT}
        />
      )}
      {isBorrowerTargetShown && (
        <DynamicValueInput
          className="grow"
          disabled={isDisabled || !isEditAllowed}
          label="Assign to borrowers"
          name={borrowerTargetsPath}
          options={assignTaskBorrowerTargetOptions}
          type={FieldTypesEnum.MULTISELECT}
          withSortedOptions={false}
          onChange={onBorrowerTargetsChange}
        />
      )}
      {isBorrowerTargetShown && borrowerTargetsHelperText && (
        <Typography color="text.secondary" variant="regular12">
          {borrowerTargetsHelperText}
        </Typography>
      )}
      {isBorrowerTargetAllowed && isEditAllowed && !isDisabled && (
        <Link
          className="self-start"
          component="button"
          type="button"
          underline="hover"
          variant="medium12"
          onClick={isBorrowerTargetShown ? onUsePrimaryBorrowerClick : onPreselectBorrowersClick}
        >
          {isBorrowerTargetShown ? USE_PRIMARY_BORROWER_LINK_TEXT : PRESELECT_BORROWERS_LINK_TEXT}
        </Link>
      )}
      {isFormsTaskType && (
        <DynamicValueInput
          required
          className="grow"
          disabled={isDisabled || !isEditAllowed}
          groupBy={onGroupOptionsBy}
          label="Loan Form"
          name={loanFormPath}
          options={formListOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}
    </div>
  );
};

LoanActionTaskRow.propTypes = {
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  options: PropTypes.array.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  label: PropTypes.string,
};

export default memo(LoanActionTaskRow);
